import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/useAuth";
import { useUser } from "@/hooks/useUser";
import { useStore } from "@/store";
import {
  Building2,
  LogOut,
  FolderOpen,
  CalendarClock,
  BookOpen,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export const UserMenu = () => {
  const { currentUser } = useUser();
  const orgName = useStore((state) => state.orgName);
  const orgRole = useStore((state) => state.orgRole);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const getInitials = () => {
    if (!currentUser?.displayName) return "U";
    return currentUser.displayName
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="relative h-8 w-8 rounded-md border min-h-[40px] min-w-[40px]"
        >
          <Avatar className="h-8 w-8 rounded-md">
            <AvatarFallback className="rounded-md bg-transparent">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {currentUser?.displayName}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {currentUser?.email}
            </p>
          </div>
        </DropdownMenuLabel>
        {orgName && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/organization")}>
              <Building2 className="mr-2 h-4 w-4" />
              <span>
                {orgName}
                {orgRole ? ` (${orgRole})` : ""}
              </span>
            </DropdownMenuItem>
            {/* Org-level, like the org itself — a brand guideline applies to
                every domain, which is why it does not live under one. */}
            <DropdownMenuItem onClick={() => navigate("/library")}>
              <FolderOpen className="mr-2 h-4 w-4" />
              <span>File library</span>
            </DropdownMenuItem>
            {/* Domain-level, unlike the library above: a job names pages and
                site connections that only exist inside one domain. */}
            <DropdownMenuItem onClick={() => navigate("/jobs")}>
              <CalendarClock className="mr-2 h-4 w-4" />
              <span>Scheduled jobs</span>
            </DropdownMenuItem>
            {/* Org-level: a recipe is authored once and run against whichever
                domain invoked it. */}
            <DropdownMenuItem onClick={() => navigate("/recipes")}>
              <BookOpen className="mr-2 h-4 w-4" />
              <span>Recipes</span>
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
